import { adminV2Participants } from "@api/all";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  DataTable,
  DataTablePagination,
  DataTableToolbar,
  SectionTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useSWRDataTable,
  useTableContext,
} from "@bleu.builders/ui";
import { format } from "date-fns";
import React from "react";

import { useReceiptUpdate } from "./context";

export function ParticipantHistory() {
  const { receipt } = useReceiptUpdate();

  const { data, error, tableState, setTableState, isLoading } = useSWRDataTable(
    adminV2Participants.receipts.path({ id: receipt.participant_id }),
    {},
    { enabled: !!receipt.participant_id },
  );

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center max-h-[80vh] min-h-[80vh] animate-pulse text-3xl">
        Wait a second. We are loading the data for you...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-between space-y-2">
        <div>
          <SectionTitle>
            <div>Something went wrong!</div>
          </SectionTitle>
          <p className="text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Participant History</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          data={data}
          error={error}
          tableState={tableState}
          setTableState={setTableState}
          setSelectedData={null}
          setQueryToParams={false}
        >
          <div className="space-y-4">
            <DataTableToolbar action={null} />
            <div className="rounded-md border dark:border-2">
              <Table>
                <TableRow>
                  <TableHead>Receipt ID</TableHead>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Points</TableHead>
                  <TableHead>Tagged with</TableHead>
                  <TableHead>Uploaded At</TableHead>
                  <TableHead>Verified At</TableHead>
                  <TableHead>Purchased At</TableHead>
                </TableRow>
                <DataTableBody />
              </Table>
            </div>
            <DataTablePagination />
          </div>
        </DataTable>
      </CardContent>
    </Card>
  );
}

type ReceiptStatus =
  | "Pending"
  | "Eligible"
  | "Processed"
  | "No Eligible Items"
  | "Needs Human Review"
  | "Rejected"
  | "Failed"
  | "Review Requested"
  | null
  | undefined;

interface StatusBadgeProps {
  status: ReceiptStatus;
}

const statusConfig: Record<
  NonNullable<ReceiptStatus>,
  { label: string; className: string }
> = {
  Pending: {
    label: "Pending",
    className: "bg-yellow-100 text-yellow-800 border-yellow-300",
  },
  Processed: {
    label: "Processed",
    className: "bg-green-100 text-green-800 border-green-300",
  },
  Eligible: {
    label: "Processed",
    className: "bg-green-100 text-green-800 border-green-300",
  },
  "No Eligible Items": {
    label: "No Eligible Items",
    className: "bg-gray-100 text-gray-800 border-gray-300",
  },
  "Needs Human Review": {
    label: "Needs Review",
    className: "bg-blue-100 text-blue-800 border-blue-300",
  },
  "Review Requested": {
    label: "Review Requested",
    className: "bg-blue-100 text-blue-800 border-blue-300",
  },
  Rejected: {
    label: "Rejected",
    className: "bg-red-100 text-red-800 border-red-300",
  },
  Failed: {
    label: "Failed",
    className: "bg-purple-100 text-purple-800 border-purple-300",
  },
};

export function StatusBadge({ status }: StatusBadgeProps) {
  if (!status) {
    return (
      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-800 border-gray-300">
        Unknown
      </span>
    );
  }

  const { label, className } = statusConfig[status];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        className,
      )}
    >
      {label}
    </span>
  );
}

function DataTableBody() {
  // @ts-expect-error TS(2339) FIXME: Property 'table' does not exist on type '{}'.
  const { table } = useTableContext();

  return (
    <TableBody>
      <>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row, index) => (
            <TableRow key={row.id}>
              <TableCell className="font-medium">{row.original.id}</TableCell>
              <TableCell>{row.original.merchant_name}</TableCell>
              <TableCell>
                $
                {row.original.total_amount
                  ? row.original.total_amount.toLocaleString()
                  : 0}
              </TableCell>
              <TableCell>
                <StatusBadge status={row.original.status} />
              </TableCell>
              <TableCell>{row.original.points}</TableCell>
              <TableCell>
                {row.original.tags ? (
                  <div className="flex flex-wrap gap-1">
                    {row.original.tags.map((tag, index) => (
                      <div
                        key={index}
                        className={cn(
                          "inline-flex items-center rounded-full border border-amber-500/50 bg-amber-50 px-3 py-1 text-sm text-amber-900",
                          {
                            "border-red-500/50 bg-red-50 text-red-900":
                              tag.data?.status === "rejected",
                          },
                        )}
                      >
                        {tag.label}
                      </div>
                    ))}
                  </div>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell>
                {format(row.original.created_at, "MMM d, yyyy HH:mm")}
              </TableCell>
              <TableCell>
                {row.original.verified_at
                  ? format(row.original.verified_at, "MMM d, yyyy HH:mm")
                  : "-"}
                {/* CHANGE TO ADMIN REVIEWED AT */}
              </TableCell>
              <TableCell>
                {row.original.purchase_date
                  ? format(row.original.purchase_date, "MMM d, yyyy HH:mm")
                  : "-"}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell className="h-24 text-center">
              <div>No results found</div>.
            </TableCell>
          </TableRow>
        )}
      </>
    </TableBody>
  );
}
